import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { AuthContext } from '../../components/AuthContext';
import MasterSelection from '../../components/MasterSelection';

function formatINR(n) {
  if (n === 0) return '—';
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  const [intPart, decPart] = abs.toFixed(2).split('.');
  const len = intPart.length;
  if (len <= 3) return sign + intPart + '.' + decPart;
  let result = intPart.slice(-3);
  let remaining = intPart.slice(0, -3);
  while (remaining.length > 2) {
    result = remaining.slice(-2) + ',' + result;
    remaining = remaining.slice(0, -2);
  }
  if (remaining.length > 0) result = remaining + ',' + result;
  return sign + result + '.' + decPart;
}

const TeleCallingPaymentManagement = () => {
  const [records, setRecords] = useState([]);
  const { user } = useContext(AuthContext);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');

  useEffect(() => {
    const interval = setInterval(() => {
      setDarkMode(localStorage.getItem('darkMode') === 'true');
    }, 500);
    return () => clearInterval(interval);
  }, []);

  const [form, setForm] = useState({
    recipientType: '',
    recipient: '',
    callResponse: '',
    remarks: '',
    reminders: { message: '', date: '' },
    createdBy: user?.user?._id,
  });

  const [selectedRecipientName, setSelectedRecipientName] = useState('');
  const [showMasterSelection, setShowMasterSelection] = useState(false);

  const [tallyData, setTallyData] = useState(null);
  const [loadingTally, setLoadingTally] = useState(false);
  const [tallyError, setTallyError] = useState(null);

  const [searchByDealer, setSearchByDealer] = useState('');

  useEffect(() => {
    const fetchRecords = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/tele-calling-payment/${user.user._id}`
        );
        const today = new Date().toISOString().split('T')[0];
        const filteredRecords = response.data.filter(
          (record) => record.createdAt.split('T')[0] === today
        );
        setRecords(filteredRecords);
      } catch (error) {
        Swal.fire('Error', 'Failed to fetch records', 'error');
      }
    };
    fetchRecords();
  }, [user]);

  const fetchTallyData = useCallback(async (recipientType, recipientId) => {
    if (!recipientType || !recipientId) {
      setTallyData(null);
      setTallyError(null);
      return;
    }

    setLoadingTally(true);
    setTallyData(null);
    setTallyError(null);

    try {
      const resp = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/tele-calling-payment/recipient-ledgers`,
        { recipientType, recipientId, createdBy: user.user._id }
      );
      const data = resp.data;
      if (data.error) {
        setTallyError(data.error);
        setTallyData(data);
      } else {
        setTallyData(data);
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to fetch tally data';
      setTallyError(msg);
      setTallyData({ ledgers: [], tallyActive: false, tallyCompanyMatch: false, totalOutstanding: 0 });
    } finally {
      setLoadingTally(false);
    }
  }, [user]);

  useEffect(() => {
    fetchTallyData(form.recipientType, form.recipient);
  }, [form.recipientType, form.recipient, fetchTallyData]);

  const handleMasterSelect = (item, entityType) => {
    const name = item.businessName || item.name || item.firmName || '';
    setSelectedRecipientName(name);
    setForm((prev) => ({
      ...prev,
      recipientType: entityType,
      recipient: item._id,
    }));
    setShowMasterSelection(false);
  };

  const clearRecipient = () => {
    setSelectedRecipientName('');
    setForm((prev) => ({
      ...prev,
      recipientType: '',
      recipient: '',
    }));
    setTallyData(null);
    setTallyError(null);
  };

  const validateForm = () => {
    const { recipientType, recipient, callResponse, remarks, reminders } = form;
    if (
      !recipientType ||
      !recipient ||
      !callResponse ||
      !remarks ||
      !reminders.message ||
      !reminders.date
    ) {
      Swal.fire('Warning', 'All fields are required', 'warning');
      return false;
    }
    return true;
  };

  const resetForm = () => {
    setForm({
      recipientType: '',
      recipient: '',
      callResponse: '',
      remarks: '',
      reminders: { message: '', date: '' },
      createdBy: user?.user?._id,
    });
    setSelectedRecipientName('');
    setTallyData(null);
    setTallyError(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('reminders.')) {
      const field = name.split('.')[1];
      setForm((prev) => ({
        ...prev,
        reminders: { ...prev.reminders, [field]: value },
      }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    try {
      const payload = {
        ...form,
        tallyLedgers: tallyData?.ledgers || [],
        totalOutstanding: tallyData?.totalOutstanding || 0,
      };
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/tele-calling-payment`,
        payload
      );
      Swal.fire('Success', 'Record created successfully', 'success');
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/tele-calling-payment/${user.user._id}`
      );
      const today = new Date().toISOString().split('T')[0];
      const filteredRecords = response.data.filter(
        (record) => record.createdAt.split('T')[0] === today
      );
      setRecords(filteredRecords);
      resetForm();
    } catch (error) {
      Swal.fire('Error', error.message, 'error');
    }
  };

  const filteredRecords = records.filter((record) => {
    if (!record.recipient) return false;
    const nameString =
      record.recipient.businessName || record.recipient.name || '';
    return nameString.toLowerCase().includes(searchByDealer.toLowerCase());
  });

  const Skeleton = ({ className = '' }) => (
    <div className={`animate-shimmer ${className}`} />
  );

  const renderTallySection = () => {
    if (!form.recipientType || !form.recipient) return null;

    if (loadingTally) {
      return (
        <div className={`mt-5 p-5 rounded-xl border animate-fade-in ${
          darkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gradient-to-br from-gray-50 to-gray-100/50'
        }`}>
          <div className="space-y-5">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="flex items-start gap-3" style={{ animationDelay: `${i * 80}ms` }}>
                <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 border-2 ${
                  i === 1
                    ? 'border-blue-500 bg-blue-500/10 animate-pulse'
                    : darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-gray-100'
                }`}>
                  {i === 1 ? (
                    <svg className="w-4 h-4 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  ) : (
                    <span className={`text-xs font-bold ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{i}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <Skeleton className="w-40 h-4 mb-1.5" />
                  <Skeleton className="w-60 h-3" />
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 justify-center pt-3">
            <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
            <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'} animate-pulse-soft`}>Connecting to Tally...</span>
          </div>
        </div>
      );
    }

    if (!tallyData) return null;

    const { steps: rawSteps, ledgers, ledgerVouchers, fyFrom, fyTo, error } = tallyData;
    const steps = rawSteps ? rawSteps.filter(s => s.step <= 4) : [];
    const hasSteps = steps.length > 0;

    const getStep = (n) => hasSteps ? steps.find(s => s.step === n) : null;
    const st = (n) => { const s = getStep(n); return s ? s.status : 'pending'; };

    const hasLedgers = ledgers && ledgers.length > 0;
    const isStepError = st(1) === 'error' || st(2) === 'error' || st(3) === 'error';
    const isError = !!tallyError || !!error || isStepError;

    const StepCircle = ({ num }) => {
      const status = st(num);
      const isDone = status === 'done';
      const isErr = status === 'error';
      const isAct = status === 'active';
      let cls = 'w-9 h-9 rounded-full flex items-center justify-center shrink-0 border-2 transition-all duration-500 ';
      if (isDone) {
        cls += 'bg-emerald-500 border-emerald-500 scale-in';
      } else if (isErr) {
        cls += 'bg-red-500 border-red-500 scale-in';
      } else if (isAct) {
        cls += 'border-blue-500 bg-blue-500/10';
      } else {
        cls += darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-gray-100';
      }
      let inner;
      if (isDone) {
        inner = <svg className="w-4 h-4 text-white animate-scale-in" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>;
      } else if (isErr) {
        inner = <svg className="w-4 h-4 text-white animate-scale-in" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" /></svg>;
      } else if (isAct) {
        inner = <svg className="w-4 h-4 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>;
      } else {
        inner = <span className={`text-xs font-bold ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{num}</span>;
      }
      return <div className={cls}>{inner}</div>;
    };

    const StepConnector = ({ num }) => {
      if (num >= 6) return null;
      const isDone = st(num) === 'done' || st(num) === 'error';
      return <div className={`w-0.5 h-6 transition-all duration-500 ${isDone ? (darkMode ? 'bg-emerald-500/30' : 'bg-emerald-300') : (darkMode ? 'bg-gray-700' : 'bg-gray-200')}`} />;
    };

    const StepBadge = ({ num }) => {
      const status = st(num);
      if (status === 'done') return <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${darkMode ? 'bg-emerald-900/50 text-emerald-400' : 'bg-emerald-100 text-emerald-700'}`}>Complete</span>;
      if (status === 'error') return <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${darkMode ? 'bg-red-900/50 text-red-400' : 'bg-red-100 text-red-700'}`}>Failed</span>;
      if (status === 'active') return <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${darkMode ? 'bg-blue-900/50 text-blue-400' : 'bg-blue-100 text-blue-700'}`}>In Progress</span>;
      return <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>Pending</span>;
    };

    const StepBody = ({ num }) => {
      if (st(num) !== 'done') return null;

      if (num === 4 && hasLedgers) {
        const lv = ledgerVouchers || {};
        return (
          <div className="mt-3 space-y-4 animate-slide-down">
            {ledgers.map(ledgerInfo => {
              const lName = ledgerInfo.ledgerName;
              const openingBal = ledgerInfo?.openingBalance || 0;
              const outstanding = ledgerInfo?.outstanding || 0;
              const entries = lv[lName] || [];
              let totDr = 0, totCr = 0;
              entries.forEach(e => { totDr += e.debit; totCr += e.credit; });
              return (
                <div key={lName} className={`rounded-lg border overflow-hidden ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className={`px-4 py-2.5 text-sm font-semibold flex items-center justify-between ${darkMode ? 'bg-gray-700/80 text-gray-300' : 'bg-gray-50 text-gray-700'}`}>
                    <span>{lName}</span>
                    {entries.length > 0 && (
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${darkMode ? 'bg-gray-600 text-gray-400' : 'bg-gray-200 text-gray-500'}`}>{entries.length} entries</span>
                    )}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className={darkMode ? 'bg-gray-800' : 'bg-gray-50'}>
                          <th className={`px-3 py-2 text-left font-semibold tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Date</th>
                          <th className={`px-3 py-2 text-left font-semibold tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Particulars</th>
                          <th className={`px-3 py-2 text-left font-semibold tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Vch Type</th>
                          <th className={`px-3 py-2 text-left font-semibold tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Vch No</th>
                          <th className={`px-3 py-2 text-right font-semibold tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Debit</th>
                          <th className={`px-3 py-2 text-right font-semibold tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Credit</th>
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${darkMode ? 'divide-gray-800' : 'divide-gray-100'}`}>
                        <tr className={darkMode ? 'bg-gray-800/30' : 'bg-gray-50/50'}>
                          <td className={`px-3 py-1.5 font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} colSpan={4}>Opening Balance</td>
                          <td className={`px-3 py-1.5 text-right tabular-nums font-medium ${openingBal > 0 ? (darkMode ? 'text-amber-400' : 'text-amber-700') : openingBal < 0 ? 'text-green-500' : ''}`} colSpan={2}>{formatINR(openingBal)}</td>
                        </tr>
                        {entries.length === 0 && (
                          <tr>
                            <td className={`px-3 py-3 text-center ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} colSpan={6}>No voucher entries for this period</td>
                          </tr>
                        )}
                        {entries.map((v, i) => (
                          <tr key={i} className={`transition-colors ${darkMode ? 'hover:bg-gray-700/30 bg-gray-800/50' : 'hover:bg-gray-50/50 bg-white'}`}>
                            <td className={`px-3 py-1.5 tabular-nums whitespace-nowrap ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{v.date}</td>
                            <td className={`px-3 py-1.5 max-w-[160px] truncate ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{v.particulars || v.vchType}</td>
                            <td className={`px-3 py-1.5 whitespace-nowrap ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{v.vchType}</td>
                            <td className={`px-3 py-1.5 whitespace-nowrap ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{v.vchNo}</td>
                            <td className={`px-3 py-1.5 text-right tabular-nums font-medium ${v.debit ? (darkMode ? 'text-amber-400' : 'text-amber-700') : ''}`}>{v.debit ? formatINR(v.debit) : '-'}</td>
                            <td className={`px-3 py-1.5 text-right tabular-nums font-medium ${v.credit ? 'text-green-500' : ''}`}>{v.credit ? formatINR(v.credit) : '-'}</td>
                          </tr>
                        ))}
                        <tr className={`font-semibold ${darkMode ? 'bg-gray-700/60' : 'bg-gray-100/80'}`}>
                          <td className={`px-3 py-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} colSpan={4}>Total</td>
                          <td className={`px-3 py-2 text-right tabular-nums ${darkMode ? 'text-amber-400' : 'text-amber-700'}`}>{formatINR(totDr)}</td>
                          <td className={`px-3 py-2 text-right tabular-nums text-green-500`}>{formatINR(totCr)}</td>
                        </tr>
                        <tr className={darkMode ? 'bg-gray-800/30' : 'bg-gray-50/50'}>
                          <td className={`px-3 py-1.5 font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} colSpan={4}>Closing Balance</td>
                          <td className={`px-3 py-1.5 text-right tabular-nums font-medium ${outstanding > 0 ? (darkMode ? 'text-amber-400' : 'text-amber-700') : outstanding < 0 ? 'text-green-500' : ''}`} colSpan={2}>{formatINR(outstanding)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className={`px-4 py-2 flex items-center justify-between border-t ${darkMode ? 'border-gray-700 bg-gray-800/30' : 'border-gray-100 bg-gray-50/30'}`}>
                    <span className={`text-xs font-semibold ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>Outstanding</span>
                    <span className={`text-sm font-bold tabular-nums ${outstanding > 0 ? (darkMode ? 'text-amber-400' : 'text-amber-700') : outstanding < 0 ? 'text-green-500' : ''}`}>
                      {formatINR(outstanding)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        );
      }

      if (num === 5 || num === 6) {
        return null;
      }

      return null;
    };

    return (
      <div className={`mt-5 rounded-xl border shadow-sm overflow-hidden transition-all duration-300 animate-slide-down ${
        isError
          ? (darkMode ? 'border-red-800 bg-red-900/20' : 'border-red-200 bg-red-50/50')
          : (darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white')
      }`}>
        <div className={`px-5 py-3.5 border-b flex items-center justify-between flex-wrap gap-2 ${
          isError
            ? (darkMode ? 'border-red-800 bg-red-900/30' : 'border-red-200 bg-red-50')
            : (darkMode ? 'border-gray-700 bg-gray-800/80' : 'border-gray-100 bg-gray-50/50')
        }`}>
          <div className="flex items-center gap-2.5">
            <div className={`w-2 h-2 rounded-full ${isError ? 'bg-red-400' : 'bg-emerald-400'}`} />
            <h4 className={`font-semibold text-sm ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
              {isError ? 'Tally Validation Failed' : 'Tally Validation Passed'}
            </h4>
          </div>
          {fyFrom && fyTo && (
            <span className={`text-[10px] ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              {fyFrom.substring(6,8)}/{fyFrom.substring(4,6)}/{fyFrom.substring(0,4)} - {fyTo.substring(6,8)}/{fyTo.substring(4,6)}/{fyTo.substring(0,4)}
            </span>
          )}
        </div>

        <div className="p-5">
          {hasSteps && (
            <div className="space-y-1">
              {steps.map(s => (
                <div key={s.step} className="animate-fade-in" style={{ animationDelay: `${(s.step - 1) * 80}ms` }}>
                  <div className="flex items-start gap-3">
                    <div className="flex flex-col items-center">
                      <StepCircle num={s.step} />
                      <StepConnector num={s.step} />
                    </div>
                    <div className="flex-1 min-w-0 pb-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>{s.name}</span>
                        <StepBadge num={s.step} />
                      </div>
                      {s.message && (
                        <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{s.message}</p>
                      )}
                      {s.detail && (
                        <div className={`mt-1.5 inline-flex items-center gap-1.5 text-[10px] px-2 py-0.5 rounded ${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                          {s.detail.host && s.detail.port && <span>{s.detail.host}:{s.detail.port}</span>}
                          {s.detail.activeCompanies && <span>{s.detail.activeCompanies.length} active, expected: {s.detail.expected}</span>}
                          {s.detail.tallyCompanyName && (
                            <span>
                              {s.detail.tallyCompanyName}
                              {s.detail.tallyUsername ? ` · ${s.detail.tallyUsername}` : ''}
                              {s.detail.tallyUser ? ` (${s.detail.tallyUser})` : ''}
                              {s.detail.tallyVersion ? ` · v${s.detail.tallyVersion}` : ''}
                            </span>
                          )}
                        </div>
                      )}
                      <StepBody num={s.step} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!hasLedgers && !isError && (
            <div className="text-center py-6">
              <div className="text-3xl mb-2 opacity-40">&#128202;</div>
              <p className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No Tally ledgers mapped to this recipient</p>
              <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Outstanding data will not be available</p>
            </div>
          )}

          {isError && (
            <div className={`mt-4 p-3 rounded-lg border flex items-center justify-between animate-scale-in ${
              darkMode ? 'bg-red-900/30 border-red-800' : 'bg-red-100/80 border-red-200'
            }`}>
              <div className="flex items-center gap-2">
                <span className="text-red-500 text-sm">&#9888;</span>
                <p className={`text-xs font-medium ${darkMode ? 'text-red-300' : 'text-red-800'}`}>{((steps || []).find(s => s.status === 'error') || {}).message || tallyError || error || 'Validation failed'}</p>
              </div>
              <button
                onClick={() => fetchTallyData(form.recipientType, form.recipient)}
                className={`shrink-0 text-xs px-3 py-1.5 rounded-md font-medium transition-colors ${
                  darkMode ? 'bg-red-800 hover:bg-red-700 text-red-200' : 'bg-red-200 hover:bg-red-300 text-red-800'
                }`}
              >
                Retry
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const cardBg = darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const headerBg = darkMode ? 'bg-gray-800/80 border-gray-700' : 'bg-gradient-to-r from-gray-50 to-white border-gray-100';
  const textPrimary = darkMode ? 'text-gray-100' : 'text-gray-800';
  const textSecondary = darkMode ? 'text-gray-400' : 'text-gray-500';
  const inputCls = `w-full px-3 py-2.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 outline-none ${
    darkMode ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
  }`;
  const labelCls = `block text-xs font-semibold mb-1.5 uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`;
  const requiredStar = <span className="text-red-500 ml-0.5">*</span>;

  return (
    <div className={`p-4 lg:p-6 max-w-7xl mx-auto space-y-6 ${darkMode ? 'bg-gray-900' : ''}`}>
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Tele-Calling Payment</h1>
          <p className={`text-sm mt-0.5 ${textSecondary}`}>Record payment collection calls with Tally outstanding details</p>
        </div>
      </div>

      <div className={`rounded-2xl shadow-sm border overflow-hidden ${cardBg}`}>
        <div className={`px-6 py-4 border-b ${headerBg}`}>
          <h3 className={`text-base font-semibold flex items-center gap-2 ${textPrimary}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
            New Call Record
          </h3>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <div>
              <label className={labelCls}>Recipient{requiredStar}</label>
              {selectedRecipientName ? (
                <div className={`flex items-center justify-between px-3 py-2.5 rounded-lg border group animate-scale-in ${
                  darkMode ? 'border-blue-700 bg-blue-900/30' : 'border-blue-200 bg-blue-50/70'
                }`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                      darkMode ? 'bg-blue-800 text-blue-300' : 'bg-blue-200 text-blue-700'
                    }`}>
                      {selectedRecipientName.charAt(0).toUpperCase()}
                    </span>
                    <div className="min-w-0">
                      <span className={`text-sm font-medium truncate block ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>{selectedRecipientName}</span>
                      <span className={`text-[10px] font-semibold uppercase tracking-wider ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>{form.recipientType}</span>
                    </div>
                  </div>
                  <button type="button" onClick={clearRecipient} className={`p-1 rounded transition-colors ${darkMode ? 'text-red-400 hover:text-red-300 hover:bg-red-900/30' : 'text-red-400 hover:text-red-600 hover:bg-red-50'}`} title="Clear">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowMasterSelection(true)}
                  className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm transition-all duration-200 ${
                    darkMode ? 'bg-gray-700 text-gray-300 border-gray-600 hover:border-blue-500 hover:text-blue-400 hover:bg-gray-650' : 'bg-white text-gray-500 border-gray-300 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50/30'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                  Select Customer or Dealer...
                </button>
              )}
            </div>

            <div>
              <label className={labelCls}>Call Response{requiredStar}</label>
              <select name="callResponse" value={form.callResponse} onChange={handleInputChange} className={inputCls}>
                <option value="">Select Response</option>
                <option value="RECEIVED">RECEIVED</option>
                <option value="NO REPLY">NO REPLY</option>
                <option value="BUSY">BUSY</option>
                <option value="NOT REACHABLE">NOT REACHABLE</option>
                <option value="SWITCH OFF">SWITCH OFF</option>
                <option value="NUMBER DOES NOT EXIST">NUMBER DOES NOT EXIST</option>
              </select>
            </div>

            <div className="sm:col-span-2 lg:col-span-1">
              <label className={labelCls}>Remarks{requiredStar}</label>
              <textarea name="remarks" value={form.remarks} onChange={handleInputChange} className={`${inputCls} resize-none`} rows={1} />
            </div>

            <div>
              <label className={labelCls}>Reminder Message{requiredStar}</label>
              <input type="text" name="reminders.message" value={form.reminders.message} onChange={handleInputChange} className={inputCls} placeholder={darkMode ? '' : 'e.g. Follow up for payment'} />
            </div>

            <div>
              <label className={labelCls}>Reminder Date{requiredStar}</label>
              <input type="date" name="reminders.date" value={form.reminders.date} onChange={handleInputChange} className={inputCls} />
            </div>
          </div>

          {renderTallySection()}

          <div className={`mt-6 flex items-center gap-3 pt-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
            <button
              onClick={handleSubmit}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-semibold shadow-sm hover:from-blue-700 hover:to-indigo-700 active:scale-[0.98] transition-all duration-200"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
              Add Record
            </button>
            {selectedRecipientName && (
              <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                Recording call for <strong className={darkMode ? 'text-gray-300' : 'text-gray-600'}>{selectedRecipientName}</strong>
              </span>
            )}
          </div>
        </div>
      </div>

      <div className={`rounded-2xl shadow-sm border overflow-hidden ${cardBg}`}>
        <div className={`px-6 py-4 border-b flex items-center justify-between flex-wrap gap-3 ${headerBg}`}>
          <h3 className={`text-base font-semibold flex items-center gap-2 ${textPrimary}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            Today&apos;s Records
            {records.length > 0 && (
              <span className={`ml-1.5 px-2 py-0.5 text-xs font-semibold rounded-full ${darkMode ? 'bg-emerald-900/40 text-emerald-400' : 'bg-emerald-100 text-emerald-700'}`}>{records.length}</span>
            )}
          </h3>
          <input
            type="text"
            className={`px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all w-full sm:w-64 ${
              darkMode ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
            }`}
            placeholder="Search by name..."
            value={searchByDealer}
            onChange={(e) => setSearchByDealer(e.target.value)}
          />
        </div>

        <div className="p-6">
          {filteredRecords.length === 0 ? (
            <div className="text-center py-10">
              <div className="text-4xl mb-3 opacity-30">&#128203;</div>
              <p className={`font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No records found for today</p>
              <p className={`text-sm mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Start by logging a new call above</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredRecords.map((record, idx) => (
                <div
                  key={record._id}
                  className={`group rounded-xl border p-4 transition-all duration-200 animate-fade-in ${
                    darkMode ? 'bg-gray-800 border-gray-700 hover:shadow-lg hover:border-gray-600' : 'bg-white border-gray-200 hover:shadow-md hover:border-gray-300'
                  }`}
                  style={{ animationDelay: `${idx * 50}ms` }}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-500 text-white flex items-center justify-center text-sm font-bold shrink-0 shadow-sm">
                        {(record.recipient?.businessName || record.recipient?.name || '?').charAt(0).toUpperCase()}
                      </span>
                      <div className="min-w-0">
                        <h4 className={`font-semibold text-sm truncate ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                          {record.recipient?.businessName || record.recipient?.name || 'N/A'}
                        </h4>
                        <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{record.recipientType}</p>
                      </div>
                    </div>
                    <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${
                      record.status === 'COMPLETED' ? (darkMode ? 'bg-emerald-900/40 text-emerald-400' : 'bg-emerald-100 text-emerald-700') :
                      record.status === 'PENDING' ? (darkMode ? 'bg-amber-900/40 text-amber-400' : 'bg-amber-100 text-amber-700') :
                      record.status === 'CANCELLED' ? (darkMode ? 'bg-red-900/40 text-red-400' : 'bg-red-100 text-red-700') :
                      darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {record.status || 'N/A'}
                    </span>
                  </div>

                  {record.tallyLedgers?.length > 0 && (
                    <div className={`mb-3 p-2.5 rounded-lg border ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gradient-to-r from-gray-50 to-gray-100/50 border-gray-100'}`}>
                      {record.tallyLedgers.map((l, i) => (
                        <div key={i} className="flex items-center justify-between text-xs py-0.5">
                          <span className={`truncate mr-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{l.ledgerName}</span>
                           <span className={`font-semibold tabular-nums whitespace-nowrap ${
                             l.outstanding > 0 ? (darkMode ? 'text-amber-400' : 'text-amber-700') : l.outstanding < 0 ? 'text-green-500' : darkMode ? 'text-gray-500' : 'text-gray-500'
                           }`}>
                            {formatINR(l.outstanding || 0)}
                          </span>
                        </div>
                      ))}
                      <div className={`flex items-center justify-between text-xs font-bold pt-1.5 mt-1.5 border-t ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                        <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>Total</span>
                         <span className={`tabular-nums ${
                           record.totalOutstanding > 0 ? (darkMode ? 'text-amber-400' : 'text-amber-700') : record.totalOutstanding < 0 ? 'text-green-500' : darkMode ? 'text-gray-400' : 'text-gray-600'
                         }`}>
                          {formatINR(record.totalOutstanding || 0)}
                        </span>
                      </div>
                    </div>
                  )}

                  <div className={`space-y-1 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <div className="flex items-center gap-1.5">
                      <span className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Response:</span>
                      <span>{record.callResponse}</span>
                    </div>
                    <div className="flex items-start gap-1.5">
                      <span className={`font-medium shrink-0 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Remarks:</span>
                      <span className="line-clamp-2">{record.remarks}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <MasterSelection
        isOpen={showMasterSelection}
        onClose={() => setShowMasterSelection(false)}
        onSelect={(item, type) => handleMasterSelect(item, type)}
        allowedTypes={['Customer', 'Dealer']}
        defaultType="Customer"
        title="Select Recipient"
      />
    </div>
  );
};

export default TeleCallingPaymentManagement;
